import Vue from 'vue';
import VueI18n from 'vue-i18n';
import VueScrollTo from 'vue-scrollto';

import initDeferredBackgrounds from '../deferred-backgrounds';
import {store} from './store';

import AddToBookmarks from './AddToBookmarks';
import NavbarSearch from './NavbarSearch.vue';
import Navigation from './Navigation';
import SideButtons from './SideButtons';
import SvgIconWhatsapp from './SvgIconWhatsapp';

require('./bootstrap-vue');

let aosModulePromise = null;

function loadAosModule() {
    if (!aosModulePromise) {
        aosModulePromise = import(/* webpackChunkName: "aos" */ 'aos').then((module) => module.default || module);
    }

    return aosModulePromise;
}

window.AOS = {
    init(...args) {
        return loadAosModule().then((AOS) => AOS.init(...args));
    }
};

initDeferredBackgrounds();

Vue.use(VueI18n);
Vue.use(VueScrollTo, {
    duration: 1000,
    offset: -80,
    easing: 'ease-in-out'
});
Vue.component('AddToBookmarks', AddToBookmarks);
Vue.component('ModalShare', () => import(/* webpackChunkName: "modal-share" */ './ModalShare'));
Vue.component('NavbarSearch', NavbarSearch);
Vue.component('Navigation', Navigation);
Vue.component('SideButtons', SideButtons);
Vue.component('SvgIconWhatsapp', SvgIconWhatsapp);

export const i18n = new VueI18n({
    locale: document.documentElement.lang,
    messages: window.translations,
    silentTranslationWarn: process.env.NODE_ENV === 'production'
});

function withValidation(componentLoader) {
    return () => Promise.all([
        import(/* webpackChunkName: "form-validation" */ './validation'),
        componentLoader()
    ]).then(([validationModule, componentModule]) => {
        const registerValidation = validationModule.default || validationModule;
        registerValidation(Vue, i18n);

        return componentModule;
    });
}

Vue.component('InquiryCallBackService', withValidation(() => import(/* webpackChunkName: "inquiry-call-back-service" */ './InquiryCallBackService')));
Vue.component('SecretSalesModalPasswordResetForm', withValidation(() => import(/* webpackChunkName: "secret-sales-modal-password-reset-form" */ './secretsales/ModalPasswordResetForm')));

function loadPageComponents() {
    const body = document.body;
    const pendingImports = [];

    if (body.classList.contains('page-property-search') || body.classList.contains('page-secret-sales')) {
        pendingImports.push(import(/* webpackChunkName: "page-property-search" */ './pages/property-search'));
    }

    if (body.classList.contains('property-detail')) {
        pendingImports.push(import(/* webpackChunkName: "page-property-detail" */ './pages/property-detail'));
    }

    if (body.classList.contains('video-direct-link')) {
        pendingImports.push(import(/* webpackChunkName: "page-property-video" */ './pages/property-video'));
    }

    if (body.classList.contains('bookmarks')) {
        pendingImports.push(import(/* webpackChunkName: "page-bookmarks" */ './pages/bookmarks'));
    }

    const contentComponentSelector = [
        'city-options',
        'city-options-slider',
        'hero-image-slider',
        'hero-slider',
        'inquiry-form',
        'modal-popup',
        'newsletter-form',
        'newsletter-slide',
        'property-filter',
        'property-item-slider',
        'reference-slider',
        'team-grid'
    ].join(',');

    if (document.querySelector(contentComponentSelector)) {
        pendingImports.push(import(/* webpackChunkName: "page-content" */ './pages/content'));
    }

    return Promise.all(pendingImports);
}

function initializeAos() {
    loadAosModule().then((AOS) => {
        AOS.init({
            disable() {
                return /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator.userAgent);
            }
        });
        document.documentElement.classList.add('aos-initialized');
    });
}

function scheduleAosInitialization() {
    const run = () => initializeAos();

    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, {timeout: 2000});
        return;
    }

    window.setTimeout(run, 0);
}

const appElement = document.getElementById('app');

if (appElement && !appElement.classList.contains('editmode')) {
    loadPageComponents()
        .then((pageModules) => {
            new Vue({
                store,
                i18n,
                mounted() {
                    this.$nextTick(() => {
                        pageModules.forEach((pageModule) => {
                            if (pageModule && typeof pageModule.afterMount === 'function') {
                                pageModule.afterMount();
                            }
                        });
                        scheduleAosInitialization();
                    });
                }
            }).$mount('#app');
        })
        .catch((error) => {
            console.error('Unable to initialize page JavaScript.', error);
        });
}
